import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Polyline, CircleMarker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { BASEMAP_URL, BASEMAP_ATTRIBUTION } from '../utils/basemap';
import {
  LatLng,
  getRoute,
  routingCostingForVehicleType,
  haversineKm,
  formatKm,
  COSTING_LABELS,
  SAME_PLACE_THRESHOLD_KM,
} from '../lib/melqarto';

/**
 * Vignette d'itinéraire d'un trajet : une vraie carte, non interactive, avec
 * le tracé prise en charge → restitution calculé par Melqarto (Valhalla)
 * selon le mode de déplacement du véhicule. Le pendant de RideRouteMap dans
 * l'app.
 *
 * La longueur affichée est celle DE L'ITINÉRAIRE entre les deux points, pas
 * la distance parcourue par le locataire — l'app ne relève aucun compteur.
 */

type RouteState =
  | { kind: 'loading' }
  | { kind: 'same-place'; at: LatLng }
  | { kind: 'points-only'; from: LatLng; to: LatLng }
  | { kind: 'routed'; from: LatLng; to: LatLng; points: [number, number][]; distanceKm: number | null };

interface RouteMapProps {
  pickup: LatLng;
  /** Absente : restitution au lieu de prise en charge. */
  dropoff?: LatLng | null;
  vehicleType?: string | null;
  className?: string;
}

const PRIMARY = '#4F46E5';

/** Cadre la carte sur ce qu'il y a à voir, à chaque changement d'état. */
const Fit: React.FC<{ state: RouteState }> = ({ state }) => {
  const map = useMap();
  useEffect(() => {
    if (state.kind === 'same-place') {
      map.setView([state.at.lat, state.at.lng], 15, { animate: false });
    } else if (state.kind === 'routed') {
      map.fitBounds(L.latLngBounds(state.points), { padding: [28, 28], animate: false });
    } else if (state.kind === 'points-only') {
      map.fitBounds(L.latLngBounds([[state.from.lat, state.from.lng], [state.to.lat, state.to.lng]]), { padding: [28, 28], animate: false });
    }
  }, [map, state]);
  return null;
};

const Dot: React.FC<{ at: LatLng; filled: boolean }> = ({ at, filled }) => (
  <CircleMarker
    center={[at.lat, at.lng]}
    radius={7}
    pathOptions={filled
      ? { color: '#ffffff', weight: 3, fillColor: PRIMARY, fillOpacity: 1 }
      : { color: PRIMARY, weight: 3, fillColor: '#ffffff', fillOpacity: 1 }}
  />
);

const RouteMap: React.FC<RouteMapProps> = ({ pickup, dropoff, vehicleType, className }) => {
  const [state, setState] = useState<RouteState>({ kind: 'loading' });
  const costing = routingCostingForVehicleType(vehicleType);
  const to = dropoff ?? pickup;

  const fromKey = `${pickup.lat},${pickup.lng}`;
  const toKey = `${to.lat},${to.lng}`;
  useEffect(() => {
    const from = pickup;
    if (haversineKm(from, to) < SAME_PLACE_THRESHOLD_KM) {
      setState({ kind: 'same-place', at: from });
      return;
    }
    if (!costing) {
      // Bateau, jetski : pas de réseau routier, on montre juste les deux points.
      setState({ kind: 'points-only', from, to });
      return;
    }
    setState({ kind: 'loading' });
    const controller = new AbortController();
    getRoute(from, to, costing, controller.signal)
      .then((route) => setState({ kind: 'routed', from, to, points: route.points, distanceKm: route.distanceKm }))
      .catch(() => { if (!controller.signal.aborted) setState({ kind: 'points-only', from, to }); });
    return () => controller.abort();
    // Clés textuelles : les objets position sont recréés à chaque rendu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromKey, toKey, costing]);

  const caption = (() => {
    switch (state.kind) {
      case 'loading': return 'Calcul de l\'itinéraire…';
      case 'same-place': return 'Prise en charge et restitution au même endroit.';
      case 'points-only': return costing
        ? 'Tracé indisponible — les deux lieux sont reliés à vol d\'oiseau.'
        : 'Hors réseau routier — les deux lieux sont reliés à vol d\'oiseau.';
      case 'routed': return state.distanceKm != null
        ? `Itinéraire de ${formatKm(state.distanceKm)} ${COSTING_LABELS[costing || 'auto']} — longueur du trajet entre les deux lieux, pas la distance parcourue.`
        : `Itinéraire ${COSTING_LABELS[costing || 'auto']}.`;
    }
  })();

  return (
    <div className={className}>
      <div className="relative h-44 overflow-hidden rounded-lg border border-gray-200">
        <MapContainer
          center={[pickup.lat, pickup.lng]}
          zoom={14}
          style={{ height: '100%', width: '100%' }}
          zoomControl={false}
          attributionControl={false}
          dragging={false}
          scrollWheelZoom={false}
          doubleClickZoom={false}
          touchZoom={false}
          keyboard={false}
        >
          <TileLayer attribution={BASEMAP_ATTRIBUTION} url={BASEMAP_URL} />
          {state.kind === 'routed' && (
            <>
              <Polyline positions={state.points} pathOptions={{ color: '#ffffff', weight: 8, opacity: 0.9 }} />
              <Polyline positions={state.points} pathOptions={{ color: PRIMARY, weight: 4, opacity: 0.95 }} />
            </>
          )}
          {state.kind === 'points-only' && (
            <Polyline
              positions={[[state.from.lat, state.from.lng], [state.to.lat, state.to.lng]]}
              pathOptions={{ color: PRIMARY, weight: 3, opacity: 0.8, dashArray: '6 8' }}
            />
          )}
          {state.kind === 'same-place' && <Dot at={state.at} filled />}
          {(state.kind === 'routed' || state.kind === 'points-only') && (
            <>
              <Dot at={state.from} filled={false} />
              <Dot at={state.to} filled />
            </>
          )}
          <Fit state={state} />
        </MapContainer>
        {state.kind === 'loading' && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/60">
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-gray-200 border-t-gray-500" aria-hidden="true" />
          </div>
        )}
      </div>
      <p className="mt-1.5 text-xs text-gray-500">{caption}</p>
    </div>
  );
};

export default RouteMap;
